import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ClipboardList, LayoutDashboard, Sparkles } from "lucide-react";
import Footer from "../components/site/Footer";

const PORTAL_FEATURES = [
  "Full client portal access",
  "Track your own expenses & income",
  "Operations: jobs, payroll, overhead & job-costing",
  "Business Finances P&L, trends & reports",
  "Documents, Workspace & spreadsheets",
  "Live chat & video support",
];

const FULL_FEATURES = [
  "Everything in Client Portal Access, plus:",
  "Hands-on financial diagnostic by Kalib & John",
  "BBB recovery roadmap & health scoring",
  "Tailored savings recommendations",
  "We review your numbers and act on your behalf",
  "Banking & payments handled for you",
];

const FULL_PLANS = [
  { name: "Reset", blurb: "Understand the business" },
  { name: "Momentum", blurb: "Improve the business" },
  { name: "Elevate", blurb: "Transform & scale the business" },
];

export default function Plans() {
  useEffect(() => {
    document.title = "Plans · Elevare Solutions";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#ded9cb] text-[#1e3d3a]">
      <header className="border-b border-black/10 bg-[#eae7de]/60 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="plans-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
            <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-black/40">Plans</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="plans-page">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Choose how we work together</p>
        <h1 className="mt-3 font-head text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">
          Two ways to use Elevare.
        </h1>
        <p className="mt-4 text-base text-black/65 leading-relaxed max-w-2xl">
          Want the tools to run your own books? Or want us in the numbers with you? Pick the fit —
          you can always upgrade to a full diagnostic later.
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Self-service */}
          <div data-testid="plan-portal-access" className="rounded-3xl border border-[#1e3d3a]/15 bg-white p-7 flex flex-col">
            <div className="flex items-center gap-2 text-[#1e3d3a]">
              <LayoutDashboard className="h-5 w-5 text-[#c9a961]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#a3823f]">Self-service</span>
            </div>
            <h2 className="mt-3 font-head text-3xl">Client Portal Access</h2>
            <p className="mt-2 text-sm text-black/60 leading-relaxed">
              Sign on for a monthly subscription and keep track of your own expenses, jobs and finances —
              no financial review required. The portal is yours.
            </p>
            <p className="mt-4 text-sm font-semibold text-[#1e3d3a]">Simple monthly subscription</p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {PORTAL_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[14px] text-[#1e3d3a]/85">
                  <Check className="h-4 w-4 text-[#6b7c3a] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <a href="mailto:kalib@elevaresolutions.me?subject=Client%20Portal%20Access" data-testid="plan-portal-cta"
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
              Get portal access
            </a>
          </div>

          {/* Full diagnostic */}
          <div data-testid="plan-full-diagnostic" className="rounded-3xl border border-[#c9a961]/50 bg-[#1e3d3a] text-white p-7 flex flex-col">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#c9a961]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#c9a961]">Full diagnostic · done with you</span>
            </div>
            <h2 className="mt-3 font-head text-3xl">Reset · Momentum · Elevate</h2>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">
              We get into your numbers — a hands-on diagnostic, a BBB recovery roadmap, and real people
              acting on your behalf. Priced per engagement after a discovery call.
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {FULL_PLANS.map((p) => (
                <div key={p.name} className="rounded-xl bg-white/[0.06] border border-white/10 px-2.5 py-3 text-center">
                  <p className="font-head text-base">{p.name}</p>
                  <p className="text-[10px] text-white/55 mt-0.5 leading-tight">{p.blurb}</p>
                </div>
              ))}
            </div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {FULL_FEATURES.map((f, i) => (
                <li key={f} className={`flex items-start gap-2.5 text-[14px] ${i === 0 ? "text-[#c9a961] font-semibold" : "text-white/85"}`}>
                  {i === 0 ? <Sparkles className="h-4 w-4 text-[#c9a961] shrink-0 mt-0.5" /> : <Check className="h-4 w-4 text-[#c9a961] shrink-0 mt-0.5" />} {f}
                </li>
              ))}
            </ul>
            <a href="mailto:kalib@elevaresolutions.me?subject=Full%20Diagnostic%20Plan" data-testid="plan-full-cta"
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#c9a961] text-[#1e3d3a] px-6 py-3.5 text-[11px] uppercase tracking-[0.18em] hover:bg-white transition-colors duration-300">
              Book a discovery call
            </a>
          </div>
        </div>

        <p className="mt-10 text-center text-[13px] text-black/55">
          Not sure which fits? <a href="mailto:kalib@elevaresolutions.me" className="underline decoration-[#c9a961] underline-offset-2 hover:text-[#1e3d3a]">Talk to Kalib or John</a> — start on portal access and upgrade whenever you're ready.
        </p>
      </main>

      <Footer />
    </div>
  );
}
